import CSDateRangePicker from '@cloudscape-design/components/date-range-picker';
import FormField from '@cloudscape-design/components/form-field';
import { DateTime } from 'luxon';
import { useMemo } from 'react';
import { Controller, useFormContext } from 'react-hook-form';

type DatepickerRangeProps = {
  name: string;
  label?: string;
  placeholder?: string;
  description?: string;
  defaultValue?: { type: string; startDate: string; endDate: string } | string;
  disabled?: boolean;
  stretch?: boolean;
  className?: string;
  rules?: any;
};

const DatepickerRange = ({
  name,
  label,
  description,
  placeholder = '',
  disabled = false,
  stretch = false,
  className = '',
}: DatepickerRangeProps) => {
  const { control, formState } = useFormContext();

  const errors = useMemo(() => {
    if (formState.errors && formState.errors[name]) {
      const value = formState.errors[name];
      if (value) {
        return Object.entries(value).map(([_key, value]) => {
          return value.message;
        });
      }
      return undefined;
    } else {
      return undefined;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formState.errors]);

  return (
    <FormField
      stretch={stretch}
      description={description}
      label={label}
      className={className}
      errorText={errors ? errors.join(' ') : undefined}
    >
      <Controller
        name={name}
        control={control}
        render={({ field }) => {
          return (
            <CSDateRangePicker
              {...field}
              disabled={disabled}
              onChange={({ detail }) => field.onChange(detail.value)}
              value={field.value || ''}
              relativeOptions={[]}
              dateOnly
              rangeSelectorMode="absolute-only"
              absoluteFormat="long-localized"
              hideTimeOffset
              isValidRange={(range) => {
                if (range?.type === 'absolute') {
                  const [startDateWithoutTime] = range.startDate.split('T');
                  const [endDateWithoutTime] = range.endDate.split('T');
                  if (!startDateWithoutTime || !endDateWithoutTime) {
                    return {
                      valid: false,
                      errorMessage:
                        'Der ausgewählte Datumsbereich ist unvollständig. Wählen Sie ein Start- und Enddatum für den Datumsbereich.',
                    };
                  }
                  if (
                    DateTime.fromISO(range.startDate)
                      .startOf('day')
                      .toSeconds() < DateTime.now().startOf('day').toSeconds()
                  ) {
                    return {
                      valid: false,
                      errorMessage:
                        'Das Startdatum muss gleich oder größer heute sein.',
                    };
                  }
                  const startDate = parseInt(
                    (new Date(range.startDate).getTime() / 1000).toFixed(0),
                  );
                  const endDate = parseInt(
                    (new Date(range.endDate).getTime() / 1000).toFixed(0),
                  );
                  if (startDate - endDate > 0) {
                    return {
                      valid: false,
                      errorMessage:
                        'Der ausgewählte Datumsbereich ist ungültig. Das Startdatum muss vor dem Enddatum liegen.',
                    };
                  }
                }
                return { valid: true };
              }}
              locale="de-DE"
              i18nStrings={{
                clearButtonLabel: 'Zurücksetzen',
                cancelButtonLabel: 'Abbrechen',
                applyButtonLabel: 'Auswählen',
              }}
              placeholder={placeholder}
            />
          );
        }}
      />
    </FormField>
  );
};

export default DatepickerRange;
